import type { IncomeSource, MonthlyLedgerEntry, MonthlyIncomeEntry, DebtAllocation } from '@/types'
import {
  PaymentRpcUnavailableError,
  type ILedgerRepository,
  type PaymentResult,
} from '@/repository/ledgerTypes'
import { diffLedgerEntry, isEmptyPatch } from '@/repository/ledgerDiff'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from './schema'

type IncomeSourceRow = Database['public']['Tables']['income_sources']['Row']
type IncomeSourceInsert = Database['public']['Tables']['income_sources']['Insert']
type LedgerEntryRow = Database['public']['Tables']['ledger_entries']['Row']
type LedgerEntryInsert = Database['public']['Tables']['ledger_entries']['Insert']
type DebtAllocationRow = Database['public']['Tables']['debt_allocations']['Row']
type DebtAllocationInsert = Database['public']['Tables']['debt_allocations']['Insert']
type MonthlyIncomeRow = Database['public']['Tables']['monthly_income_entries']['Row']
type MonthlyIncomeInsert = Database['public']['Tables']['monthly_income_entries']['Insert']

/**
 * Cloud-backed ledger repository — the most involved of the four repos
 * because a single `MonthlyLedgerEntry` spans three tables:
 *
 *   - `ledger_entries`           one row per month, the parent
 *   - `debt_allocations`         one row per debt per month
 *   - `monthly_income_entries`   one row per income source per month
 *
 * Reads use PostgREST resource embedding (`select(*, child(*))`) so the
 * whole entry hydrates in one round-trip. v0.8.56: everyday edits go
 * through `saveLedgerChanges` (row- and field-level updates, so a household
 * member's saves to other rows survive); whole-month saves (`upsertLedgerEntry`,
 * used by close / reopen / start month) upsert children then prune. Child
 * rows cascade on delete, so `deleteLedgerEntry` is a one-row delete.
 */
export class SupabaseLedgerRepo implements ILedgerRepository {
  private readonly client: SupabaseClient<Database>
  private readonly userId: string
  private readonly workspaceId: string

  constructor(client: SupabaseClient<Database>, userId: string, workspaceId: string) {
    this.client = client
    this.userId = userId
    this.workspaceId = workspaceId
  }

  // ─── Income sources ──────────────────────────────────────────────────

  async getAllIncomeSources(): Promise<IncomeSource[]> {
    const { data, error } = await this.client
      .from('income_sources')
      .select('*')
      .eq('workspace_id', this.workspaceId)
      .order('created_at', { ascending: true })

    if (error) throw new Error(`[income_sources.getAll] ${error.message}`)
    return (data ?? []).map(rowToIncomeSource)
  }

  async getIncomeSourceById(id: string): Promise<IncomeSource | null> {
    const { data, error } = await this.client
      .from('income_sources')
      .select('*')
      .eq('id', id)
      .eq('workspace_id', this.workspaceId)
      .maybeSingle()

    if (error) throw new Error(`[income_sources.getById] ${error.message}`)
    return data ? rowToIncomeSource(data) : null
  }

  async upsertIncomeSource(source: IncomeSource): Promise<IncomeSource> {
    const row = incomeSourceToInsert(source, this.userId, this.workspaceId)
    const { data, error } = await this.client
      .from('income_sources')
      .upsert(row, { onConflict: 'id' })
      .select()
      .single()

    if (error) throw new Error(`[income_sources.upsert] ${error.message}`)
    return rowToIncomeSource(data)
  }

  async deleteIncomeSource(id: string): Promise<void> {
    const { error } = await this.client
      .from('income_sources')
      .delete()
      .eq('id', id)
      .eq('workspace_id', this.workspaceId)

    if (error) throw new Error(`[income_sources.delete] ${error.message}`)
  }

  async bulkReplaceIncomeSources(sources: IncomeSource[]): Promise<void> {
    const { error: delErr } = await this.client
      .from('income_sources')
      .delete()
      .eq('workspace_id', this.workspaceId)
    if (delErr) throw new Error(`[income_sources.bulkReplace:delete] ${delErr.message}`)

    if (sources.length === 0) return

    const rows = sources.map((s) => incomeSourceToInsert(s, this.userId, this.workspaceId))
    const { error: insErr } = await this.client.from('income_sources').insert(rows)
    if (insErr) throw new Error(`[income_sources.bulkReplace:insert] ${insErr.message}`)
  }

  // ─── Ledger entries ──────────────────────────────────────────────────

  async getAllLedgerEntries(): Promise<MonthlyLedgerEntry[]> {
    const { data, error } = await this.client
      .from('ledger_entries')
      .select('*, debt_allocations(*), monthly_income_entries(*)')
      .eq('workspace_id', this.workspaceId)
      .order('month', { ascending: true })

    if (error) throw new Error(`[ledger_entries.getAll] ${error.message}`)
    return (data ?? []).map((row) =>
      hydrateLedgerEntry(
        row,
        (row as unknown as { debt_allocations: DebtAllocationRow[] }).debt_allocations ?? [],
        (row as unknown as { monthly_income_entries: MonthlyIncomeRow[] }).monthly_income_entries ??
          []
      )
    )
  }

  async getLedgerEntryByMonth(month: string): Promise<MonthlyLedgerEntry | null> {
    const { data, error } = await this.client
      .from('ledger_entries')
      .select('*, debt_allocations(*), monthly_income_entries(*)')
      .eq('workspace_id', this.workspaceId)
      .eq('month', month)
      .maybeSingle()

    if (error) throw new Error(`[ledger_entries.getByMonth] ${error.message}`)
    if (!data) return null
    return hydrateLedgerEntry(
      data,
      (data as unknown as { debt_allocations: DebtAllocationRow[] }).debt_allocations ?? [],
      (data as unknown as { monthly_income_entries: MonthlyIncomeRow[] }).monthly_income_entries ??
        []
    )
  }

  async upsertLedgerEntry(entry: MonthlyLedgerEntry): Promise<MonthlyLedgerEntry> {
    // 1. Upsert the parent row.
    const parentRow = ledgerEntryToInsert(entry, this.userId, this.workspaceId)
    const { error: parentErr } = await this.client
      .from('ledger_entries')
      .upsert(parentRow, { onConflict: 'id' })
    if (parentErr) throw new Error(`[ledger_entries.upsert:parent] ${parentErr.message}`)

    // 2. Upsert child rows, then prune rows no longer in the entry.
    //    v0.8.56 — was delete-everything-then-insert: two separate requests,
    //    so a connection drop between them left the month with no rows in
    //    the cloud. Upsert-then-prune never has a moment where kept rows
    //    are missing.
    if (entry.allocations.length > 0) {
      const allocRows = entry.allocations.map((a) => allocationToInsert(a, entry.id, this.userId))
      const { error } = await this.client
        .from('debt_allocations')
        .upsert(allocRows, { onConflict: 'ledger_entry_id,debt_id' })
      if (error) throw new Error(`[ledger_entries.upsert:allocations] ${error.message}`)
    }
    await this.pruneChildren(
      'debt_allocations',
      'debt_id',
      entry.id,
      entry.allocations.map((a) => a.debtId)
    )

    if (entry.income.length > 0) {
      const incRows = entry.income.map((i) => incomeEntryToInsert(i, entry.id, this.userId))
      const { error } = await this.client
        .from('monthly_income_entries')
        .upsert(incRows, { onConflict: 'ledger_entry_id,source_id' })
      if (error) throw new Error(`[ledger_entries.upsert:income] ${error.message}`)
    }
    await this.pruneChildren(
      'monthly_income_entries',
      'source_id',
      entry.id,
      entry.income.map((i) => i.sourceId)
    )

    return entry
  }

  /** Deletes a month's child rows whose key isn't in `keep`. */
  private async pruneChildren(
    table: 'debt_allocations' | 'monthly_income_entries',
    keyColumn: 'debt_id' | 'source_id',
    entryId: string,
    keep: string[]
  ): Promise<void> {
    let query = this.client.from(table).delete().eq('ledger_entry_id', entryId)
    if (keep.length > 0) query = query.not(keyColumn, 'in', `(${keep.join(',')})`)
    const { error } = await query
    if (error) throw new Error(`[ledger_entries.upsert:prune_${table}] ${error.message}`)
  }

  /**
   * v0.8.56 — everyday edits send only what changed (see ledgerDiff.ts).
   * Each changed row gets an UPDATE of just its changed columns; a row that
   * no longer exists remotely is inserted from this device's full copy.
   */
  async saveLedgerChanges(before: MonthlyLedgerEntry, after: MonthlyLedgerEntry): Promise<void> {
    const patch = diffLedgerEntry(before, after)
    if (!patch) {
      await this.upsertLedgerEntry(after)
      return
    }
    if (isEmptyPatch(patch)) return
    const id = after.id

    await Promise.all([
      ...patch.allocations.map((c) =>
        this.updateOrInsert(
          'debt_allocations',
          id,
          'debt_id',
          c.debtId,
          allocationChangesToColumns(c.changes),
          allocationToInsert(c.full, id, this.userId)
        )
      ),
      ...patch.income.map((c) =>
        this.updateOrInsert(
          'monthly_income_entries',
          id,
          'source_id',
          c.sourceId,
          incomeChangesToColumns(c.changes),
          incomeEntryToInsert(c.full, id, this.userId)
        )
      ),
    ])

    if (patch.addedAllocations.length > 0) {
      const { error } = await this.client.from('debt_allocations').upsert(
        patch.addedAllocations.map((a) => allocationToInsert(a, id, this.userId)),
        { onConflict: 'ledger_entry_id,debt_id', ignoreDuplicates: true }
      )
      if (error) throw new Error(`[ledger_entries.save:add_allocations] ${error.message}`)
    }
    if (patch.addedIncome.length > 0) {
      const { error } = await this.client.from('monthly_income_entries').upsert(
        patch.addedIncome.map((i) => incomeEntryToInsert(i, id, this.userId)),
        { onConflict: 'ledger_entry_id,source_id', ignoreDuplicates: true }
      )
      if (error) throw new Error(`[ledger_entries.save:add_income] ${error.message}`)
    }
    if (patch.removedDebtIds.length > 0) {
      const { error } = await this.client
        .from('debt_allocations')
        .delete()
        .eq('ledger_entry_id', id)
        .in('debt_id', patch.removedDebtIds)
      if (error) throw new Error(`[ledger_entries.save:remove_allocations] ${error.message}`)
    }
    if (patch.removedSourceIds.length > 0) {
      const { error } = await this.client
        .from('monthly_income_entries')
        .delete()
        .eq('ledger_entry_id', id)
        .in('source_id', patch.removedSourceIds)
      if (error) throw new Error(`[ledger_entries.save:remove_income] ${error.message}`)
    }
  }

  private async updateOrInsert(
    table: 'debt_allocations' | 'monthly_income_entries',
    entryId: string,
    keyColumn: 'debt_id' | 'source_id',
    key: string,
    columns: Record<string, unknown>,
    fullRow: DebtAllocationInsert | MonthlyIncomeInsert
  ): Promise<void> {
    const { data, error } = await this.client
      .from(table)
      .update(columns as never)
      .eq('ledger_entry_id', entryId)
      // Union-typed table/column defeats the typed builder; values are fixed literals.
      .eq(keyColumn as never, key as never)
      .select('ledger_entry_id')
    if (error) throw new Error(`[ledger_entries.save:${table}] ${error.message}`)
    if (data && data.length > 0) return
    const { error: insErr } = await this.client.from(table).upsert(fullRow as never, {
      onConflict: `ledger_entry_id,${keyColumn}`,
      ignoreDuplicates: true,
    })
    if (insErr) throw new Error(`[ledger_entries.save:${table}:insert] ${insErr.message}`)
  }

  /**
   * v0.8.56 — atomic payment via migration 007's `log_payment` RPC.
   * Throws PaymentRpcUnavailableError when the migration isn't applied so
   * the store can fall back to the pre-v0.8.56 path.
   */
  async logPayment(
    entryId: string,
    debtId: string,
    expected: number | null,
    next: number | null
  ): Promise<PaymentResult> {
    const { data, error } = await this.client.rpc('log_payment', {
      p_entry: entryId,
      p_debt: debtId,
      p_expected: expected,
      p_new: next,
    })
    if (error) {
      if (error.code === 'PGRST202' || /could not find the function/i.test(error.message)) {
        throw new PaymentRpcUnavailableError(error.message)
      }
      throw new Error(error.message)
    }
    const row = Array.isArray(data) ? data[0] : data
    if (!row) throw new Error('Payment was not saved — empty response from the server.')
    return {
      status: row.result === 'conflict' ? 'conflict' : 'ok',
      actualPayment: row.actual === null ? null : Number(row.actual),
      balance: Number(row.new_balance),
    }
  }

  async deleteLedgerEntry(id: string): Promise<void> {
    // ON DELETE CASCADE on the child tables handles the rest.
    const { error } = await this.client
      .from('ledger_entries')
      .delete()
      .eq('id', id)
      .eq('workspace_id', this.workspaceId)

    if (error) throw new Error(`[ledger_entries.delete] ${error.message}`)
  }

  async bulkReplaceLedgerEntries(entries: MonthlyLedgerEntry[]): Promise<void> {
    // Wipe everything — children cascade.
    const { error: delErr } = await this.client
      .from('ledger_entries')
      .delete()
      .eq('workspace_id', this.workspaceId)
    if (delErr) throw new Error(`[ledger_entries.bulkReplace:delete] ${delErr.message}`)

    if (entries.length === 0) return

    // Insert parents in one batch first so child FKs are satisfied.
    const parentRows = entries.map((e) => ledgerEntryToInsert(e, this.userId, this.workspaceId))
    const { error: parentErr } = await this.client.from('ledger_entries').insert(parentRows)
    if (parentErr) {
      throw new Error(`[ledger_entries.bulkReplace:insert_parents] ${parentErr.message}`)
    }

    const allocRows: DebtAllocationInsert[] = []
    const incRows: MonthlyIncomeInsert[] = []
    for (const entry of entries) {
      for (const a of entry.allocations) {
        allocRows.push(allocationToInsert(a, entry.id, this.userId))
      }
      for (const i of entry.income) {
        incRows.push(incomeEntryToInsert(i, entry.id, this.userId))
      }
    }

    if (allocRows.length > 0) {
      const { error: allocErr } = await this.client.from('debt_allocations').insert(allocRows)
      if (allocErr) {
        throw new Error(`[ledger_entries.bulkReplace:insert_allocations] ${allocErr.message}`)
      }
    }

    if (incRows.length > 0) {
      const { error: incErr } = await this.client.from('monthly_income_entries').insert(incRows)
      if (incErr) {
        throw new Error(`[ledger_entries.bulkReplace:insert_income] ${incErr.message}`)
      }
    }
  }
}

// ─── Row ↔ entity mappers ────────────────────────────────────────────────

function rowToIncomeSource(row: IncomeSourceRow): IncomeSource {
  const source: IncomeSource = {
    id: row.id,
    workspaceId: row.workspace_id,
    name: row.name,
    type: row.type,
    stability: row.stability,
    isActive: row.is_active,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
  if (row.default_amount !== null) source.defaultAmount = Number(row.default_amount)
  if (row.notes !== null) source.notes = row.notes
  return source
}

function incomeSourceToInsert(
  source: IncomeSource,
  userId: string,
  workspaceId: string
): IncomeSourceInsert {
  return {
    id: source.id,
    workspace_id: workspaceId,
    user_id: userId,
    name: source.name,
    type: source.type,
    stability: source.stability,
    default_amount: source.defaultAmount ?? null,
    is_active: source.isActive,
    notes: source.notes ?? null,
    created_at: source.createdAt,
    updated_at: source.updatedAt,
  }
}

function hydrateLedgerEntry(
  row: LedgerEntryRow,
  allocationRows: DebtAllocationRow[],
  incomeRows: MonthlyIncomeRow[]
): MonthlyLedgerEntry {
  const entry: MonthlyLedgerEntry = {
    id: row.id,
    workspaceId: row.workspace_id,
    month: row.month,
    status: row.status,
    income: incomeRows.map(rowToIncomeEntry),
    allocations: allocationRows.map(rowToAllocation),
    createdAt: row.created_at,
  }
  if (row.essential_expenses !== null) entry.essentialExpenses = Number(row.essential_expenses)
  if (row.notes !== null) entry.notes = row.notes
  if (row.closed_at !== null) entry.closedAt = row.closed_at
  return entry
}

function rowToAllocation(row: DebtAllocationRow): DebtAllocation {
  const alloc: DebtAllocation = {
    debtId: row.debt_id,
    startingBalance: Number(row.starting_balance),
    effectiveAPR: Number(row.effective_apr),
    monthMinimumPayment: Number(row.month_minimum_payment),
    plannedPayment: Number(row.planned_payment),
  }
  if (row.month_minimum_payment_override !== null) {
    alloc.monthMinimumPaymentOverride = Number(row.month_minimum_payment_override)
  }
  if (row.actual_payment !== null) alloc.actualPayment = Number(row.actual_payment)
  if (row.ending_balance_calculated !== null) {
    alloc.endingBalanceCalculated = Number(row.ending_balance_calculated)
  }
  if (row.ending_balance_override !== null) {
    alloc.endingBalanceOverride = Number(row.ending_balance_override)
  }
  if (row.interest_accrued !== null) alloc.interestAccrued = Number(row.interest_accrued)
  if (row.balance_before_close !== null) {
    alloc.balanceBeforeClose = Number(row.balance_before_close)
  }
  if (row.notes !== null) alloc.notes = row.notes
  return alloc
}

function rowToIncomeEntry(row: MonthlyIncomeRow): MonthlyIncomeEntry {
  const entry: MonthlyIncomeEntry = {
    sourceId: row.source_id,
    amount: Number(row.amount),
  }
  if (row.notes !== null) entry.notes = row.notes
  return entry
}

function ledgerEntryToInsert(
  entry: MonthlyLedgerEntry,
  userId: string,
  workspaceId: string
): LedgerEntryInsert {
  return {
    id: entry.id,
    workspace_id: workspaceId,
    user_id: userId,
    month: entry.month,
    status: entry.status,
    essential_expenses: entry.essentialExpenses ?? null,
    notes: entry.notes ?? null,
    created_at: entry.createdAt,
    closed_at: entry.closedAt ?? null,
  }
}

function allocationToInsert(
  alloc: DebtAllocation,
  ledgerEntryId: string,
  userId: string
): DebtAllocationInsert {
  const insert: DebtAllocationInsert = {
    ledger_entry_id: ledgerEntryId,
    debt_id: alloc.debtId,
    user_id: userId,
    starting_balance: alloc.startingBalance,
    effective_apr: alloc.effectiveAPR,
    month_minimum_payment: alloc.monthMinimumPayment,
    month_minimum_payment_override: alloc.monthMinimumPaymentOverride ?? null,
    planned_payment: alloc.plannedPayment,
    actual_payment: alloc.actualPayment ?? null,
    ending_balance_calculated: alloc.endingBalanceCalculated ?? null,
    ending_balance_override: alloc.endingBalanceOverride ?? null,
    interest_accrued: alloc.interestAccrued ?? null,
    notes: alloc.notes ?? null,
  }
  // v0.8.31 — ONLY include `balance_before_close` when it actually has a
  // value to write. Pre-fix this always wrote `null`, which required the
  // column to exist on the cloud table. Projects that hadn't applied the
  // v0.8.23 migration (`20260514_001_add_balance_before_close.sql`) had
  // EVERY ledger upsert rejected by Postgres ("column 'balance_before_close'
  // does not exist") — including `resetMonthFromRegistry`, which is what
  // surfaced this. Reopen-symmetric restore needs the column to be there
  // when it's actually written, but plain planning-month edits don't.
  // Omitting the field when undefined lets the basic ledger path work
  // even pre-migration.
  if (alloc.balanceBeforeClose !== undefined) {
    insert.balance_before_close = alloc.balanceBeforeClose
  }
  return insert
}

/** DebtAllocation field → debt_allocations column (v0.8.56 field-level saves). */
const ALLOCATION_COLUMNS: Record<keyof DebtAllocation, string | null> = {
  debtId: null,
  startingBalance: 'starting_balance',
  effectiveAPR: 'effective_apr',
  monthMinimumPayment: 'month_minimum_payment',
  monthMinimumPaymentOverride: 'month_minimum_payment_override',
  plannedPayment: 'planned_payment',
  actualPayment: 'actual_payment',
  endingBalanceCalculated: 'ending_balance_calculated',
  endingBalanceOverride: 'ending_balance_override',
  interestAccrued: 'interest_accrued',
  balanceBeforeClose: 'balance_before_close',
  // Local-only reopen snapshot; not a cloud column.
  paidOffAtBeforeClose: null,
  notes: 'notes',
}

function allocationChangesToColumns(changes: Partial<DebtAllocation>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(changes)) {
    const column = ALLOCATION_COLUMNS[key as keyof DebtAllocation]
    if (column) out[column] = value ?? null
  }
  return out
}

function incomeChangesToColumns(changes: Partial<MonthlyIncomeEntry>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  if ('amount' in changes) out.amount = changes.amount ?? 0
  if ('notes' in changes) out.notes = changes.notes ?? null
  return out
}

function incomeEntryToInsert(
  income: MonthlyIncomeEntry,
  ledgerEntryId: string,
  userId: string
): MonthlyIncomeInsert {
  return {
    ledger_entry_id: ledgerEntryId,
    source_id: income.sourceId,
    user_id: userId,
    amount: income.amount,
    notes: income.notes ?? null,
  }
}
